import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { paymentAPI, formatCurrency, formatDate } from '../../services/api';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import PrintableReceipt from '../../components/Reports/PrintableReceipt';
import { useToast } from '../../contexts/ToastContext';

const PaymentDetails = () => {
  const { id } = useParams();

  const toast = useToast();
  const { data, isLoading, error } = useQuery(
    ['payment', id],
    () => paymentAPI.getPayment(id),
    {
      enabled: !!id,
      onError: (e) => toast.error(e?.message || 'Failed to load payment'),
      refetchOnMount: 'always',
      refetchOnWindowFocus: true,
      staleTime: 0,
      cacheTime: 0,
    }
  );
  const { data: receipt } = useQuery(
    ['payment-receipt', id],
    () => paymentAPI.generateReceipt(id),
    {
      enabled: !!id,
      onError: () => toast.error('Failed to load receipt'),
      refetchOnMount: 'always',
      refetchOnWindowFocus: true,
      staleTime: 0,
      cacheTime: 0,
    }
  );

  if (isLoading) return <LoadingSpinner size="lg" />;
  if (error) return <div className="py-12 text-center text-red-600">{error.message}</div>;

  const p = data?.data?.data;
  const r = receipt?.data?.data;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-primary-700">Payment Details</h1>
        <Link to="/payments" className="text-sm btn btn-ghost">Back to Payments</Link>
      </div>

      {p && (
        <div className="bg-white rounded shadow">
          <div className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <div className="text-sm text-gray-500">Payment #</div>
              <div className="text-lg font-semibold">{p.payment_number}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Date</div>
              <div className="text-lg font-semibold">{formatDate(p.payment_date)}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Amount</div>
              <div className="text-lg font-semibold">{formatCurrency(p.amount)}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Type</div>
              <div className="text-lg font-semibold capitalize">{p.payment_type?.replace('_', ' ')}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Principal</div>
              <div className="text-lg font-semibold">{formatCurrency(p.principal_amount)}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Interest</div>
              <div className="text-lg font-semibold">{formatCurrency(p.interest_amount)}</div>
            </div>
          </div>
        </div>
      )}

      {r && (
        <div className="bg-white rounded shadow p-4">
          <h3 className="text-lg font-semibold mb-2">Receipt Info</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <div className="text-sm text-gray-500">Outstanding Principal</div>
              <div className="text-lg font-semibold">{formatCurrency(r.updated_outstanding?.outstanding_principal || 0)}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Outstanding Interest</div>
              <div className="text-lg font-semibold">{formatCurrency(r.updated_outstanding?.outstanding_interest || 0)}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Total Outstanding</div>
              <div className="text-lg font-semibold text-primary-700">{formatCurrency(r.updated_outstanding?.total_outstanding || 0)}</div>
            </div>
          </div>

          {(r.months_covered_by_this_payment?.length || 0) > 0 && (
            <div className="mt-4">
              <div className="text-sm text-gray-500 mb-1">Months Covered</div>
              <div className="text-sm">{r.months_covered_by_this_payment.join(', ')}</div>
            </div>
          )}

          {(r.months_pending_as_of_payment_date?.length || 0) > 0 && (
            <div className="mt-2">
              <div className="text-sm text-gray-500 mb-1">Pending Months</div>
              <div className="text-sm">{r.months_pending_as_of_payment_date.join(', ')}</div>
            </div>
          )}
        </div>
      )}

      {/* Printable Customer Copy */}
      {p && (
        <div className="bg-white rounded shadow p-4">
          <h3 className="text-lg font-semibold mb-2">Customer Copy</h3>
          <PrintableReceipt
            id={`payment-receipt-${p.id}`}
            receipt={{ number: p.payment_number, date: p.payment_date, loanNumber: p.loan_number, paymentNumber: p.payment_number }}
            customer={{
              name: `${p.first_name || ''} ${p.last_name || ''}`.trim(),
              fatherOrSpouse: p.father_name || p.guardian_name || '',
              address: `${p.address_line_1 || ''} ${p.address_line_2 || ''} ${p.city || ''} ${p.state || ''} ${p.pincode || ''}`.trim(),
              mobile1: p.mobile_1,
              mobile2: p.mobile_2,
              aadhar: p.aadhar_number,
              code: p.customer_code,
            }}
            loan={{
              amount: p.amount,
              interestRate: typeof p.interest_rate === 'number' ? p.interest_rate : undefined,
              agreement: p.remarks,
              itemsText: r?.pledged_items_text || '',
            }}
            meta={{ paidMonths: r?.months_covered_by_this_payment || [] }}
          />
        </div>
      )}
    </div>
  );
};

export default PaymentDetails;


